import { useState } from "react";
import { Link2, Loader2, Mail, MessageCircle, Phone, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { PARTNER_TYPE_META, formatDoc, formatPhone } from "../partner-meta";
import { PartnerConnectDialog } from "../PartnerConnectDialog";

type Cadastro = {
  id: string;
  type: keyof typeof PARTNER_TYPE_META;
  name: string | null;
  tradeName: string | null;
  email: string | null;
  phone: string | null;
  document: string | null;
  croNumber: string | null;
  croState: string | null;
  status: "ACTIVE" | "INACTIVE";
  linkedOrganizationId: string | null;
  isPartner: boolean;
};

const selo = "rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide";

/** Quem é o cadastrado, os selos (tipo, conectado, parceira, inativo), o contato e as ações. */
export function CabecalhoDoCadastro({ cadastro: c }: { cadastro: Cadastro }) {
  const utils = trpc.useUtils();
  const [convidando, setConvidando] = useState(false);
  const meta = PARTNER_TYPE_META[c.type];
  const invalidar = () => {
    utils.partner.getById.invalidate({ id: c.id });
    utils.partner.list.invalidate();
    utils.partner.countByType.invalidate();
  };
  const desativar = trpc.partner.disable.useMutation({ onSuccess: () => (toast.success("Cadastro desativado."), invalidar()), onError: (e) => toast.error(e.message) });
  const reativar = trpc.partner.activate.useMutation({ onSuccess: () => (toast.success("Cadastro reativado."), invalidar()), onError: (e) => toast.error(e.message) });
  const telefone = c.phone?.replace(/\D/g, "");

  return (
    <header className="flex flex-col gap-4 rounded-xl border border-zinc-200 bg-white p-5 shadow-sm sm:flex-row sm:items-start dark:border-border dark:bg-card">
      <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}>
        <meta.icon className="h-7 w-7" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={selo} style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}>{meta.label}</span>
          {c.linkedOrganizationId && <span className={`${selo} bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400`}>Conectado</span>}
          {c.isPartner && <span className={`${selo} bg-brand-50 text-brand-700 dark:bg-primary/10 dark:text-primary`}>Clínica parceira</span>}
          {c.status === "INACTIVE" && <span className={`${selo} bg-zinc-100 text-zinc-500 dark:bg-muted dark:text-muted-foreground`}>Inativo</span>}
        </div>
        <p className="mt-1.5 truncate text-xl font-semibold text-zinc-900 dark:text-foreground">{c.name || meta.label}</p>
        {c.tradeName && <p className="truncate text-sm text-zinc-500 dark:text-muted-foreground">{c.tradeName}</p>}
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-zinc-600 dark:text-muted-foreground">
          {c.croNumber && <span>CRO {c.croNumber}{c.croState ? `/${c.croState}` : ""}</span>}
          {c.document && <span>{formatDoc(c.document) || c.document}</span>}
          {telefone && (
            <span className="inline-flex items-center gap-1">
              <Phone className="h-3.5 w-3.5" />
              {formatPhone(telefone)}
              <a href={`https://wa.me/55${telefone}`} target="_blank" rel="noreferrer" aria-label="Abrir no WhatsApp" className="ml-0.5 text-emerald-600 hover:text-emerald-700">
                <MessageCircle className="h-3.5 w-3.5" />
              </a>
            </span>
          )}
          {c.email && (
            <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-zinc-900 dark:hover:text-foreground">
              <Mail className="h-3.5 w-3.5" />
              {c.email}
            </a>
          )}
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap gap-2 sm:flex-col sm:items-end">
        {c.type === "DENTIST" && !c.linkedOrganizationId && (
          <button
            type="button"
            onClick={() => setConvidando(true)}
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-brand-200 bg-brand-50 px-3 text-[12px] font-semibold text-brand-700 hover:bg-brand-100 dark:border-primary/40 dark:bg-primary/10 dark:text-primary"
          >
            <Link2 className="h-3.5 w-3.5" />
            Convidar pra plataforma
          </button>
        )}
        {c.status === "ACTIVE" ? (
          <button
            type="button"
            disabled={desativar.isPending}
            onClick={() => confirm("Desativar este cadastro?") && desativar.mutate({ id: c.id })}
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-red-200 px-3 text-[12px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-60 dark:border-red-500/30 dark:hover:bg-red-500/10"
          >
            {desativar.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            Desativar
          </button>
        ) : (
          <button
            type="button"
            disabled={reativar.isPending}
            onClick={() => reativar.mutate({ id: c.id })}
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-emerald-200 px-3 text-[12px] font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-60 dark:border-emerald-500/30 dark:text-emerald-400"
          >
            {reativar.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
            Reativar
          </button>
        )}
      </div>
      <PartnerConnectDialog
        partner={convidando ? { id: c.id, name: c.name ?? meta.label, phone: c.phone, email: c.email } : null}
        onClose={() => setConvidando(false)}
      />
    </header>
  );
}
